// GenieX Villa Experience — shared config: rooms, layout, scenes, reducer.
// Kept framework-agnostic (no JSX) so both the full Experience page and any
// future mini embed (e.g. Value Packs "see it in your home" widget) can pull
// a filtered subset of ROOMS / ROOM_BOX / initialState without duplication.
import { Tv, Sparkles, Flame, Moon, Sun, Droplets, Sunrise, Film, PartyPopper, Plane } from "lucide-react";

export const ROOMS = ["living", "dining", "kitchen", "bedroom1", "bedroom2", "bath1", "bath2"];

export const roomLabel = {
  living: "Living Room",
  dining: "Dining",
  kitchen: "Kitchen",
  bedroom1: "Bedroom 1",
  bedroom2: "Bedroom 2",
  bath1: "Bathroom 1",
  bath2: "Bathroom 2",
};

export const roomIcon = {
  living: Tv,
  dining: Sparkles,
  kitchen: Flame,
  bedroom1: Moon,
  bedroom2: Sun,
  bath1: Droplets,
  bath2: Droplets,
};

// Room bounding boxes in the villa SVG coordinate space (viewBox 0 0 900 640).
// Circulation logic: Main Gate -> Driveway -> Main Door -> Living Room (foyer) ->
// Dining -> Kitchen (one open run along the top), and Living -> Bedroom 2 directly
// below it. Bedroom 1 sits under Dining/Kitchen with Bathroom 1 attached to its
// south wall; Bathroom 2 is attached to Bedroom 2's east wall. Every room shares
// a wall with at least one neighbour — no isolated rooms.
export const ROOM_BOX = {
  living:   { x: 40,  y: 60,  w: 380, h: 250, name: "Living Room" },
  dining:   { x: 440, y: 60,  w: 200, h: 140, name: "Dining" },
  kitchen:  { x: 660, y: 60,  w: 200, h: 140, name: "Kitchen" },
  bedroom1: { x: 440, y: 220, w: 420, h: 200, name: "Bedroom 1" },
  bath1:    { x: 660, y: 440, w: 200, h: 80,  name: "Bathroom 1" },
  bedroom2: { x: 40,  y: 330, w: 260, h: 180, name: "Bedroom 2" },
  bath2:    { x: 320, y: 440, w: 140, h: 80,  name: "Bathroom 2" },
};

// Utility fixture (not a selectable room) — replaces the old Garage slot.
export const WATER_TANK_BOX = { x: 40, y: 530, w: 260, h: 90, name: "Water Tank" };

// Main Door sits on Living Room's south (exterior) wall; a short walkway leads
// from it, through the garden gap, into the driveway where the Main Gate sits.
export const MAIN_DOOR_POS = { x: 300, y: ROOM_BOX.living.y + ROOM_BOX.living.h };
export const GATE_POS = { x: 590, y: 594 };
export const DRIVEWAY_BOX = { x: 300, y: 528, w: 560, h: 92 };

// Robot vacuum: one physical unit, docked in the Bedroom 2 / Water Tank utility
// nook, travelling room-to-room through the same doorway adjacencies above —
// never teleporting or crossing a wall that doesn't have an opening.
export const VACUUM_DOCK = { x: WATER_TANK_BOX.x + WATER_TANK_BOX.w - 26, y: WATER_TANK_BOX.y - 14 };
export const VACUUM_ROUTE = ["living", "dining", "kitchen", "bedroom1", "bedroom2"];
export const VACUUM_WAYPOINTS = {
  living:   { x: 230, y: 260 },
  dining:   { x: 540, y: 130 },
  kitchen:  { x: 760, y: 130 },
  bedroom1: { x: 650, y: 320 },
  bedroom2: { x: 170, y: 470 },
};

// Corridor stop(s) inserted between two stops so the vacuum's path never
// visibly glides straight through a room it isn't docked/cleaning in — each
// entry only passes through the shared unclaimed floor gap between the two
// room boxes (see ROOM_BOX comment above for the wall layout).
export const VACUUM_CORRIDOR = {
  "dock->living": [{ x: 230, y: 320 }],
  "living->dining": [{ x: 410, y: 170 }],
  "dining->kitchen": [],
  "kitchen->bedroom1": [],
  "bedroom1->bedroom2": [{ x: 300, y: 420 }],
  "bedroom2->dock": [],
};

export const initialState = {
  time: "evening", // morning | day | evening | night
  scene: null, // Currently active scene id (null when idle)
  gate: "closed", // closed | opening | open — vehicle gate at the driveway entrance
  lock: "locked", // locked | unlocked — main door smart lock (face recognition)
  doorbell: false, // ringing overlay
  alarm: null, // 'fire' | 'gas' | 'water' | null
  waterTank: { level: 42, motorOn: false, autoMode: true },
  vacuum: { status: "docked", room: null, progress: 0, done: [] }, // docked | cleaning | returning
  rooms: {
    living:   { light: 0, ac: false, tv: false, fan: 0, curtains: 0, temp: 24 },
    dining:   { light: 0, fan: 0 },
    kitchen:  { light: 0, appliance: false, fan: 0 },
    bedroom1: { light: 0, ac: false, tv: false, fan: 0, curtains: 0, temp: 22 },
    bedroom2: { light: 0, ac: false, fan: 0, curtains: 0, temp: 24 },
    bath1:    { light: 0, geyser: false },
    bath2:    { light: 0, geyser: false },
  },
};

export function reducer(state, action) {
  switch (action.type) {
    case "PATCH_ROOM": {
      const { room, patch } = action;
      return { ...state, rooms: { ...state.rooms, [room]: { ...state.rooms[room], ...patch } } };
    }
    case "PATCH_VACUUM":
      return { ...state, vacuum: { ...state.vacuum, ...action.patch } };
    case "RESET_ROOM": {
      const r = state.rooms[action.room];
      const next = { ...r, light: 0 };
      if ("ac" in next) next.ac = false;
      if ("tv" in next) next.tv = false;
      if ("fan" in next) next.fan = 0;
      if ("curtains" in next) next.curtains = 0;
      if ("geyser" in next) next.geyser = false;
      if ("appliance" in next) next.appliance = false;
      return { ...state, rooms: { ...state.rooms, [action.room]: next } };
    }
    case "PATCH_WATER_TANK":
      return { ...state, waterTank: { ...state.waterTank, ...action.patch } };
    case "TICK_WATER_TANK": {
      const next = Math.min(100, state.waterTank.level + 4);
      const shouldStop = state.waterTank.autoMode && next >= 96;
      return {
        ...state,
        waterTank: { ...state.waterTank, level: shouldStop ? 100 : next, motorOn: shouldStop ? false : state.waterTank.motorOn },
      };
    }
    case "SET": {
      return { ...state, ...action.patch };
    }
    case "SCENE_START":
      return { ...state, scene: action.scene };
    case "SCENE_END":
      return { ...state, scene: null };
    case "RESET":
      return { ...initialState, time: state.time };
    default:
      return state;
  }
}

/* ──────────────────────── Scene Engine ────────────────────────── */

// Each scene is a list of {at (ms), do (fn(dispatch))}. Steps run in order —
// staggered on purpose so a scene feels choreographed, not instant.
export const buildScenes = () => ({
  "good-morning": {
    label: "Good Morning",
    icon: Sunrise,
    accent: "#f9c274",
    steps: [
      { at: 0,    do: (d) => d({ type: "SET", patch: { time: "morning" } }) },
      { at: 300,  do: (d) => d({ type: "PATCH_ROOM", room: "bedroom1", patch: { curtains: 100 } }) },
      { at: 700,  do: (d) => d({ type: "PATCH_ROOM", room: "bedroom1", patch: { light: 45 } }) },
      { at: 1000, do: (d) => d({ type: "PATCH_ROOM", room: "bath1",  patch: { light: 70, geyser: true } }) },
      { at: 1600, do: (d) => d({ type: "PATCH_ROOM", room: "kitchen", patch: { light: 85, appliance: true } }) },
      { at: 2100, do: (d) => d({ type: "PATCH_ROOM", room: "living", patch: { light: 30, curtains: 100 } }) },
      { at: 2700, do: (d) => d({ type: "PATCH_ROOM", room: "dining", patch: { light: 45, fan: 1 } }) },
    ],
  },
  "good-night": {
    label: "Good Night",
    icon: Moon,
    accent: "#8ea1ff",
    steps: [
      { at: 0,    do: (d) => d({ type: "SET", patch: { time: "night", lock: "locked", gate: "closed" } }) },
      { at: 300,  do: (d) => d({ type: "PATCH_ROOM", room: "living", patch: { light: 0, tv: false, ac: false, fan: 0, curtains: 0 } }) },
      { at: 600,  do: (d) => d({ type: "PATCH_ROOM", room: "dining", patch: { light: 0, fan: 0 } }) },
      { at: 800,  do: (d) => d({ type: "PATCH_ROOM", room: "kitchen", patch: { light: 0, appliance: false } }) },
      { at: 1100, do: (d) => d({ type: "PATCH_ROOM", room: "bedroom2",  patch: { light: 0, curtains: 0 } }) },
      { at: 1400, do: (d) => d({ type: "PATCH_ROOM", room: "bedroom1", patch: { light: 12, ac: true, curtains: 0, fan: 1 } }) },
      { at: 1800, do: (d) => d({ type: "PATCH_ROOM", room: "bath1", patch: { light: 8 } }) },
    ],
  },
  movie: {
    label: "Movie Mode",
    icon: Film,
    accent: "#c084fc",
    steps: [
      { at: 0,    do: (d) => d({ type: "SET", patch: { time: "night" } }) },
      { at: 200,  do: (d) => d({ type: "PATCH_ROOM", room: "living", patch: { curtains: 0, light: 8, tv: true, ac: true, fan: 1 } }) },
      { at: 700,  do: (d) => d({ type: "PATCH_ROOM", room: "dining", patch: { light: 5 } }) },
      { at: 1000, do: (d) => d({ type: "PATCH_ROOM", room: "kitchen", patch: { light: 0 } }) },
    ],
  },
  party: {
    // Zoned, not "everything blasted": Living = the party (RGB glow, handled in
    // VillaCanvas when scene === 'party'), bedrooms stay dim/comfortable,
    // dining gets a soft ambient lift, exterior pathway lights come on.
    label: "Party",
    icon: PartyPopper,
    accent: "#f472b6",
    steps: [
      { at: 0,    do: (d) => d({ type: "SET", patch: { time: "night" } }) },
      { at: 100,  do: (d) => d({ type: "PATCH_ROOM", room: "living", patch: { light: 70, tv: true, fan: 2, curtains: 100 } }) },
      { at: 400,  do: (d) => d({ type: "PATCH_ROOM", room: "dining", patch: { light: 40, fan: 1 } }) },
      { at: 700,  do: (d) => d({ type: "PATCH_ROOM", room: "kitchen", patch: { light: 45 } }) },
      { at: 1000, do: (d) => d({ type: "PATCH_ROOM", room: "bedroom2",  patch: { light: 22 } }) },
      { at: 1300, do: (d) => d({ type: "PATCH_ROOM", room: "bedroom1", patch: { light: 25 } }) },
    ],
  },
  away: {
    label: "Away",
    icon: Plane,
    accent: "#94a3b8",
    steps: [
      { at: 0, do: (d) => d({ type: "SET", patch: { lock: "locked", gate: "closed" } }) },
      ...ROOMS.map((r, i) => ({
        at: 200 + i * 150,
        do: (d) => d({ type: "RESET_ROOM", room: r }),
      })),
      { at: 200 + ROOMS.length * 150 + 150, do: (d) => d({ type: "PATCH_WATER_TANK", patch: { motorOn: false } }) },
    ],
  },
  vacation: {
    label: "Vacation",
    icon: Sun,
    accent: "#22d3ee",
    steps: [
      { at: 0, do: (d) => d({ type: "SET", patch: { lock: "locked", gate: "closed" } }) },
      ...ROOMS.map((r, i) => ({
        at: 100 + i * 120,
        do: (d) => d({ type: "RESET_ROOM", room: r }),
      })),
      // Simulate presence — living room light pulses subtly (single toggle demo)
      { at: 1600, do: (d) => d({ type: "PATCH_ROOM", room: "living", patch: { light: 25 } }) },
      { at: 3200, do: (d) => d({ type: "PATCH_ROOM", room: "living", patch: { light: 0 } }) },
    ],
  },
});

export const sceneBlurb = {
  "good-morning":  "Sunrise · curtains, kitchen, geyser",
  "good-night":    "Everything off · Bedroom 1 soft glow",
  movie:    "Cinema dim · TV + AC on",
  party:    "Living Room RGB · bedrooms stay dim",
  away:     "Lights off · gate closed · water motor off",
  vacation: "Presence simulation · gate closed",
};
